type CellValue = null | 'player' | 'ai';
type Board = CellValue[][];

const ROWS = 6;
const COLS = 7;

// Enhanced evaluation weights for different patterns
const WEIGHTS = {
  FOUR: 100000,    // Winning move
  THREE: 1000,     // Three in a row with space
  TWO: 100,        // Two in a row with spaces
  CENTER: 30,      // Control of center column
  THREAT: -800,    // Opponent's three in a row
  BLOCK: 500      // Blocking opponent's potential win
};

const evaluateWindow = (window: CellValue[], player: 'player' | 'ai'): number => {
  const opponent = player === 'player' ? 'ai' : 'player';
  const playerCount = window.filter(cell => cell === player).length;
  const opponentCount = window.filter(cell => cell === opponent).length;
  const emptyCount = window.filter(cell => cell === null).length;

  // Winning move
  if (playerCount === 4) return WEIGHTS.FOUR;
  
  // Immediate block needed
  if (opponentCount === 3 && emptyCount === 1) return WEIGHTS.BLOCK;
  
  // Threatening position
  if (playerCount === 3 && emptyCount === 1) return WEIGHTS.THREE;
  
  // Building position
  if (playerCount === 2 && emptyCount === 2) return WEIGHTS.TWO;
  
  // Opponent threat
  if (opponentCount === 3 && emptyCount === 1) return WEIGHTS.THREAT;

  return 0;
};

const evaluatePosition = (board: Board, player: 'player' | 'ai'): number => {
  let score = 0;

  // Horizontal
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS - 3; col++) {
      const window = board[row].slice(col, col + 4);
      score += evaluateWindow(window, player);
    }
  }

  // Vertical
  for (let col = 0; col < COLS; col++) {
    for (let row = 0; row < ROWS - 3; row++) {
      const window = [
        board[row][col],
        board[row + 1][col],
        board[row + 2][col],
        board[row + 3][col]
      ];
      score += evaluateWindow(window, player);
    }
  }

  // Diagonal (positive slope)
  for (let row = 0; row < ROWS - 3; row++) {
    for (let col = 0; col < COLS - 3; col++) {
      const window = [
        board[row][col],
        board[row + 1][col + 1],
        board[row + 2][col + 2],
        board[row + 3][col + 3]
      ];
      score += evaluateWindow(window, player);
    }
  }

  // Diagonal (negative slope)
  for (let row = 3; row < ROWS; row++) {
    for (let col = 0; col < COLS - 3; col++) {
      const window = [
        board[row][col],
        board[row - 1][col + 1],
        board[row - 2][col + 2],
        board[row - 3][col + 3]
      ];
      score += evaluateWindow(window, player);
    }
  }

  // Bonus for center control
  for (let row = 0; row < ROWS; row++) {
    if (board[row][3] === player) {
      score += WEIGHTS.CENTER;
    }
  }

  return score;
};

const findLowestEmptyRow = (board: Board, col: number): number => {
  for (let row = ROWS - 1; row >= 0; row--) {
    if (!board[row][col]) {
      return row;
    }
  }
  return -1;
};

const isWinningMove = (board: Board, row: number, col: number, player: CellValue): boolean => {
  const directions = [
    [0, 1],  // horizontal
    [1, 0],  // vertical
    [1, 1],  // diagonal right
    [1, -1]  // diagonal left
  ];

  for (const [dr, dc] of directions) {
    let count = 1;
    
    // Check forward
    for (let i = 1; i < 4; i++) {
      const r = row + dr * i;
      const c = col + dc * i;
      if (r < 0 || r >= ROWS || c < 0 || c >= COLS) break;
      if (board[r][c] !== player) break;
      count++;
    }
    
    // Check backward
    for (let i = 1; i < 4; i++) {
      const r = row - dr * i;
      const c = col - dc * i;
      if (r < 0 || r >= ROWS || c < 0 || c >= COLS) break;
      if (board[r][c] !== player) break;
      count++;
    }

    if (count >= 4) return true;
  }
  return false;
};

const minimax = (
  board: Board,
  depth: number,
  alpha: number,
  beta: number,
  isMaximizing: boolean,
  lastMove: { row: number; col: number } | null
): number => {
  // Check for winning move in last position
  if (lastMove && isWinningMove(board, lastMove.row, lastMove.col, isMaximizing ? 'player' : 'ai')) {
    return isMaximizing ? -WEIGHTS.FOUR : WEIGHTS.FOUR;
  }

  if (depth === 0) {
    return evaluatePosition(board, 'ai') - evaluatePosition(board, 'player');
  }

  const validMoves = Array.from({ length: COLS }, (_, i) => i)
    .filter(col => findLowestEmptyRow(board, col) !== -1);

  if (validMoves.length === 0) return 0;

  if (isMaximizing) {
    let maxScore = -Infinity;
    for (const col of validMoves) {
      const row = findLowestEmptyRow(board, col);
      board[row][col] = 'ai';
      const score = minimax(board, depth - 1, alpha, beta, false, { row, col });
      board[row][col] = null;
      maxScore = Math.max(maxScore, score);
      alpha = Math.max(alpha, score);
      if (beta <= alpha) break;
    }
    return maxScore;
  } else {
    let minScore = Infinity;
    for (const col of validMoves) {
      const row = findLowestEmptyRow(board, col);
      board[row][col] = 'player';
      const score = minimax(board, depth - 1, alpha, beta, true, { row, col });
      board[row][col] = null;
      minScore = Math.min(minScore, score);
      beta = Math.min(beta, score);
      if (beta <= alpha) break;
    }
    return minScore;
  }
};

export const findBestMove = (board: Board, difficulty: string): number => {
  const depthMap = {
    'easy': 3,
    'medium': 5,
    'hard': 7
  };
  
  const depth = depthMap[difficulty as keyof typeof depthMap] || 5;
  let bestScore = -Infinity;
  let bestMove = 3; // Default to center column
  
  // Prioritize center and near-center columns
  const moveOrder = [3, 2, 4, 1, 5, 0, 6];
  
  for (const col of moveOrder) {
    const row = findLowestEmptyRow(board, col);
    if (row !== -1) {
      board[row][col] = 'ai';
      
      // Check for immediate win
      if (isWinningMove(board, row, col, 'ai')) {
        board[row][col] = null;
        return col;
      }
      
      const score = minimax(board, depth, -Infinity, Infinity, false, { row, col });
      board[row][col] = null;
      
      if (score > bestScore) {
        bestScore = score;
        bestMove = col;
      }
    }
  }
  
  return bestMove;
};